import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

// material-ui
import { styled, useTheme, Theme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';

// project imports
import Header from './Header';
import Sidebar from './Sidebar';
import { drawerWidth } from 'store/constant';
import { SET_MENU } from 'store/actions';
import { DefaultRootStateProps } from 'types';
import { MenuToggleButton } from './Header/MenuToggleButton';
import WhatsAppDrawer from './Header/WhatsAppDrawer';
import HubInboxDrawer from './Header/HubInboxDrawer';
import AiAssistantDrawer from './Header/AiAssistantDrawer';
import AiDocumentCreatorBridge from './Header/AiDocumentCreatorBridge';
import AiTextToDataBridge from './Header/AiTextToDataBridge';
import WhatsNewGate from 'views/main-page/WhatsNewGate';
import { getWhatsappConfig, isOfficialHubServer } from '../../hooks/useWhatsAppConfig';
import { useAiAssistantAvailability } from '../../hooks/useAiAssistentInfo';
import useMasterDataRefresh from '../../hooks/useMasterDataRefresh';

// assets

interface MainStyleProps {
    theme: Theme;
    open: boolean;
}

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }: MainStyleProps) => ({
    ...theme.typography.mainContent,
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        margin: 0,
        padding: 0,
        height: 'calc(100vh)',

        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: -drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px'
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px'
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('lg'));

    const leftDrawerOpened = useSelector((state: DefaultRootStateProps) => state.customization.opened);
    const dispatch = useDispatch();
    const handleLeftDrawerToggle = React.useCallback(() => {
        dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
    }, [dispatch, leftDrawerOpened]);

    React.useEffect(() => {
        dispatch({ type: SET_MENU, opened: !matchDownMd });
    }, [matchDownMd]);

    useMasterDataRefresh();

    const waConfig = useMemo(() => getWhatsappConfig(), []);
    const aiAssistant = useAiAssistantAvailability();
    const showAiAssistant = aiAssistant.available;

    const [drawerOpenSignal, setDrawerOpenSignal] = useState(0);
    const [aiOpenSignal, setAiOpenSignal] = useState(0);

    useEffect(() => {
        const aiHandler = () => setAiOpenSignal((v) => v + 1);
        window.addEventListener('ai-open-drawer', aiHandler);
        return () => {
            window.removeEventListener('ai-open-drawer', aiHandler);
        };
    }, []);

    useEffect(() => {
        const waOpen = () => setDrawerOpenSignal((v) => v + 1);
        window.addEventListener('wa-open-drawer', waOpen);
        return () => window.removeEventListener('wa-open-drawer', waOpen);
    }, []);

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <WhatsNewGate />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: theme.palette.background.default,
                    transition: leftDrawerOpened ? theme.transitions.create('width') : 'none'
                }}
            >
                {leftDrawerOpened ? (
                    <Toolbar>
                        <Header leftDrawerOpened={leftDrawerOpened} handleLeftDrawerToggle={handleLeftDrawerToggle} />
                    </Toolbar>
                ) : (
                    <Box
                        sx={{
                            top: 20,
                            position: 'fixed',
                            left: `3%`,
                            zIndex: 10
                        }}
                    >
                        <MenuToggleButton leftDrawerOpened={leftDrawerOpened} handleLeftDrawerToggle={handleLeftDrawerToggle} />
                    </Box>
                )}
            </AppBar>

            {/* drawer */}
            <Sidebar drawerOpen={leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} />

            {/* main content */}
            <Main theme={theme} open={leftDrawerOpened}>
                <Outlet />
            </Main>

            {/* WhatsApp drawer + floating FAB — always rendered */}
            {waConfig?.isNewApi && (
                <WhatsAppDrawer
                    baseUrl={waConfig.baseUrl}
                    token={waConfig.token}
                    sessionId={waConfig.instanceId}
                    openSignal={drawerOpenSignal}
                />
            )}
            {isOfficialHubServer(waConfig?.server) && waConfig && (
                <HubInboxDrawer waConfig={waConfig} />
            )}

            {showAiAssistant && (
                <>
                    <AiAssistantDrawer openSignal={aiOpenSignal} />
                    {/* Handles "create document" / navigate / open-send-dialog
                        requests from the AI chat iframe (host-bridge). */}
                    <AiDocumentCreatorBridge />
                    {/* Legacy fallback: opens the native smart drawer when the
                        compact docs tab asks for the "big drawer" and no
                        draft exists yet (see AiTextToDataBridge docstring). */}
                    <AiTextToDataBridge />
                </>
            )}
        </Box>
    );
};

export default MainLayout;
